import { type NextRouter, type NextUrlObject } from './next-types';
import { useRouter as useTSRRouter, useLocation } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import qs from 'querystring';

function searchParamsToRecord(searchParams: string): Record<string, unknown> {
  return qs.parse(searchParams.replace(/^\?/, ''));
}

const addMissingParamsFromSearchParams = (
  query: Record<string, unknown>,
  searchParams: string
): Record<string, unknown> => ({
  ...query,
  ...searchParamsToRecord(searchParams)
});

const urlToString = (url: string | NextUrlObject): string => {
  if (typeof url === 'string') return url;

  const query = url.query ? qs.stringify(url.query) : '';
  const search = url.search ?? (query ? `?${query}` : '');
  const hash = url.hash && !url.hash.startsWith('#') ? `#${url.hash}` : (url.hash ?? '');

  return `${url.pathname ?? ''}${search}${hash}`;
};

const createEventEmitter = () => {
  const events: Record<string, Function[]> = {};

  return {
    on: (event: string, listener: Function) => {
      if (!events[event]) {
        events[event] = [];
      }
      events[event].push(listener);
    },
    off: (event: string, listener: Function) => {
      if (!events[event]) return;
      events[event] = events[event].filter((l) => l !== listener);
    },
    emit: async (event: string, args: any) => {
      if (!events[event]) return true;
      for (const listener of events[event]) {
        const result = await listener(args);
        if (!result) return false;
      }
      return true;
    }
  };
};

const eventEmitter = createEventEmitter();

const Router: NextRouter = {
  push: (url) =>
    new Promise((resolve) => {
      window.history.pushState({}, '', urlToString(url));

      resolve(true);
    }),
  replace: (url) =>
    new Promise((resolve) => {
      window.history.replaceState({}, '', urlToString(url));

      resolve(true);
    }),
  reload: () => window.location.reload(),
  back: () => {},
  forward: () => {},
  prefetch: (url) =>
    new Promise((resolve) => {
      window.fetch(url);

      resolve();
    }),
  beforePopState: () => {},
  events: {
    on: eventEmitter.on,
    off: eventEmitter.off,
    emit: eventEmitter.emit
  },
  isFallback: false,
  isReady: true,
  isLocaleDomain: false,
  locale: undefined,
  defaultLocale: undefined,
  isPreview: false,
  route: window.location.pathname,
  pathname: window.location.pathname,
  asPath: window.location.pathname,
  basePath: '',
  query: Object.fromEntries(new URLSearchParams(window.location.search)),
  domainLocales: [],
  locales: []
};

export const useRouter = (): NextRouter => {
  const { basepath, navigate, history } = useTSRRouter();
  const { pathname, search, href } = useLocation();

  const [hrefState, setHrefState] = useState(href);

  useEffect(() => {
    setHrefState(href);
  }, [href]);

  const searchHash = JSON.stringify(search);

  return useMemo(
    () => ({
      basePath: basepath,
      route: pathname,
      query: search,
      asPath: hrefState,
      push: async (url) => {
        const nav =
          typeof url === 'string'
            ? { to: String(url) }
            : {
                to: String(url.pathname),
                search: addMissingParamsFromSearchParams(
                  url.query ?? {},
                  String(url.search ?? '')
                )
              };
        const canProceed = await eventEmitter.emit(
          'routeChangeStart',
          typeof url === 'string' ? url : String(url.pathname) + (url.search ?? '')
        );
        return canProceed && !navigate(nav);
      },
      replace: async (url) => {
        const nav =
          typeof url === 'string'
            ? { to: String(url), replace: true }
            : {
                to: url.pathname !== undefined ? String(url.pathname) : pathname,
                search: addMissingParamsFromSearchParams(
                  url.query ?? {},
                  String(url.search ?? '')
                ),
                replace: true
              };
        const canProceed = await eventEmitter.emit(
          'routeChangeStart',
          typeof url === 'string' ? url : String(url.pathname ?? pathname) + (url.search ?? '')
        );
        return canProceed && !navigate(nav);
      },
      reload: window.location.reload,
      back: () => history.back(),
      forward: () => history.forward(),
      prefetch: async () => {
        throw new Error('router.prefetch() not implemented');
      },
      beforePopState: async () => {
        throw new Error('router.beforePopState() not implemented');
      },
      events: {
        on: eventEmitter.on,
        off: eventEmitter.off,
        emit: eventEmitter.emit
      },
      isFallback: false,
      isReady: true,
      isLocaleDomain: false,
      locale: undefined,
      defaultLocale: undefined,
      domainLocales: [],
      locales: [],
      isPreview: false,
      pathname
    }),
    [basepath, navigate, history, pathname, searchHash, hrefState]
  );
};

export default Router;
